import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import LoginForm from "./LoginForm";
import RegisterForm from "./RegisterForm";
import ResetPasswordForm from "./ResetPasswordForm";
import { X } from "lucide-react";
import { cn } from "@/utils/cn";
import { useModalA11y } from "@/utils/useModalA11y";


export default function AuthModal({ isOpen, onClose, initialView = "login", message }) {
  console.log('[DIAG] AuthModal render, isOpen=', isOpen);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [view, setView] = useState(initialView);
  const [contentHeight, setContentHeight] = useState(0);

  const contentRef = useRef(null);
  const dialogRef = useRef(null);

  // Sync `mounted`/`view`/`visible` to the isOpen/initialView props directly
  // during render (React's recommended derived-state pattern) — only the
  // actual animation timing below (waiting for a paint before triggering
  // the entry transition, delaying unmount for the exit transition) is a
  // genuine external-system side effect that belongs in an effect.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const [prevInitialView, setPrevInitialView] = useState(initialView);
  if (isOpen !== prevIsOpen || initialView !== prevInitialView) {
    console.log('[DIAG] derived-state block firing: isOpen=', isOpen, 'prevIsOpen=', prevIsOpen, 'initialView=', initialView, 'prevInitialView=', prevInitialView);
    setPrevIsOpen(isOpen);
    setPrevInitialView(initialView);
    if (isOpen) {
      setMounted(true);
      setView(initialView);
    } else {
      setVisible(false);
    }
  }
  console.log('[DIAG] mounted=', mounted, 'visible=', visible);

  // Handle mounting and visibility states for the entry/exit animations
  useEffect(() => {
    console.log('[DIAG] effect RUN, isOpen at effect-run-time=', isOpen);
    if (isOpen) {
      // Double requestAnimationFrame ensures the browser paints before we trigger the transition
      const raf1 = requestAnimationFrame(() => {
        requestAnimationFrame(() => { console.log('[DIAG] setVisible(true) firing'); setVisible(true); });
      });
      return () => { console.log('[DIAG] effect CLEANUP (isOpen branch), cancelling raf'); cancelAnimationFrame(raf1); };
    } else {
      console.log('[DIAG] scheduling setMounted(false) timer');
      const timer = setTimeout(() => { console.log('[DIAG] setMounted(false) FIRING'); setMounted(false); }, 300);
      return () => { console.log('[DIAG] effect CLEANUP (else branch), clearing timer'); clearTimeout(timer); };
    }
  }, [isOpen, initialView]);

  // Use ResizeObserver to calculate the height of the form dynamically
  useEffect(() => {
    if (!contentRef.current || !mounted) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        // We use blockSize (height) to animate the modal container
        setContentHeight(entry.borderBoxSize[0].blockSize);
      }
    });
    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [mounted]);

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  useModalA11y(mounted, handleClose, dialogRef);

  if (!mounted) return null;

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/60 backdrop-blur-xl transition-opacity duration-300",
        visible ? "opacity-100" : "opacity-0"
      )}
      onClick={handleClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Authentication"
        className={cn(
          "bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl w-[400px] relative overflow-hidden transition-all duration-300 ease-in-out",
          visible ? "scale-100 opacity-100" : "scale-95 opacity-0"
        )}
        style={{ height: contentHeight || 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div ref={contentRef} className="p-8">
          <button
            onClick={handleClose}
            aria-label="Close dialog"
            className="absolute top-4 right-4 p-2 text-zinc-500 hover:text-zinc-200 transition-colors rounded-full hover:bg-zinc-800 z-50"
          >
            <X size={18} />
          </button>

          {message && (
            <p className="mb-4 text-center text-sm text-amber-400 bg-amber-500/10 p-3 rounded-lg border border-amber-500/20">
              {message}
            </p>
          )}

          {/* AnimatePresence handles the exit animation. 
            mode="wait" ensures the old form leaves before the new one slides in.
          */}
          <AnimatePresence mode="wait">
            <motion.div
              key={view}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              {view === "login" ? (
                <LoginForm 
                  onSuccess={handleClose} 
                  onSwitchToRegister={() => setView("register")} 
                  onSwitchToReset={() => setView("reset")}
                />
              ) : view === "register" ? (
                <RegisterForm 
                  onSuccess={handleClose} 
                  onSwitchToLogin={() => setView("login")} 
                />
              ) : (
                <ResetPasswordForm 
                  onSwitchToLogin={() => setView("login")}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}